import { memo } from 'react';
import { Box, Button, Flex, Icon, Text, Tooltip } from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { MdSettings } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';

import LogoutIcon from 'components/Icons/LogoutIcon';
import UserProfileIcon from 'components/Icons/UserProfileIcon';
import TextComponent from 'components/Text';
import { getUserData, getUserSubscriptionLimits, SetUserModalVisible } from 'redux/UserSlice';

const getUserInitials = (firstname?: string, lastname?: string) => {
  const first = firstname?.charAt(0)?.toUpperCase() || '';
  const last = lastname?.charAt(0)?.toUpperCase() || '';
  return `${first}${last}` || 'U';
};

export type UserMenuPanelProperties = {
  /** Close popover, drawer, or other parent container after an action. */
  onRequestClose?: () => void;
  /** Parent owns the logout confirmation modal. */
  onLogoutRequest: () => void;
};

const UserMenuPanel = memo(({ onRequestClose, onLogoutRequest }: UserMenuPanelProperties) => {
  const dispatch: any = useDispatch();
  const userData = useSelector(getUserData);
  const userSubscriptionLimits = useSelector(getUserSubscriptionLimits);

  const openUserModal = () => {
    onRequestClose?.();
    dispatch(SetUserModalVisible(true));
  };

  const handleLogout = () => {
    onRequestClose?.();
    onLogoutRequest();
  };

  return (
    <>
      <Box
        as='button'
        type='button'
        w='100%'
        textAlign='left'
        p={4}
        onClick={openUserModal}
        _hover={{ bg: 'gray.50' }}
        borderRadius='xl'
        transition='background 0.15s'
      >
        <Flex alignItems='center' gap={4}>
          <Box
            boxSize='52px'
            borderRadius='12px'
            bg='#F5F0FF'
            display='flex'
            alignItems='center'
            justifyContent='center'
          >
            <Text fontSize='20px' fontWeight='600' color='#8B5CF6'>
              {getUserInitials(userData?.firstname, userData?.lastname)}
            </Text>
          </Box>
          <Box flex={1} overflow='hidden' minW={0}>
            <Tooltip label={`${userData?.firstname} ${userData?.lastname}`}>
              <Box>
                <TextComponent ellipsis={true} as='h2' fontWeight='700' fontSize='lg' color='black'>
                  {`${userData?.firstname} ${userData?.lastname}`}
                </TextComponent>
              </Box>
            </Tooltip>
            <Text fontSize='sm' color='gray.500' mt={0.5}>
              {userSubscriptionLimits?.current_subscription_name}
            </Text>
          </Box>
          <ChevronRightIcon boxSize={5} color='black' flexShrink={0} />
        </Flex>
      </Box>
      <Box borderTop='1px solid' borderColor='gray.100' px={3} py={3}>
        <Flex gap={2} justifyContent='center'>
          <Tooltip label='Profile' hasArrow placement='top'>
            <Button
              aria-label='Profile'
              size='sm'
              bg='#F1EEFF'
              borderRadius='8px'
              width='63px'
              height='46px'
              minW='63px'
              p='8px'
              _hover={{ bg: '#E0DAFE' }}
              onClick={openUserModal}
            >
              <UserProfileIcon width={20} height={20} color='#745BE3' />
            </Button>
          </Tooltip>
          <Tooltip label='Settings' hasArrow placement='top'>
            <Button
              aria-label='Settings'
              size='sm'
              bg='#F1EEFF'
              borderRadius='8px'
              width='63px'
              height='46px'
              minW='63px'
              p='8px'
              _hover={{ bg: '#E0DAFE' }}
              onClick={openUserModal}
            >
              <Icon as={MdSettings} color='#745BE3' boxSize={5} />
            </Button>
          </Tooltip>
          <Tooltip label='Log out' hasArrow placement='top'>
            <Button
              aria-label='Log out'
              size='sm'
              bg='#F1EEFF'
              borderRadius='8px'
              width='63px'
              height='46px'
              minW='63px'
              p='8px'
              _hover={{ bg: '#E0DAFE' }}
              onClick={handleLogout}
            >
              <LogoutIcon width={20} height={20} color='#745BE3' />
            </Button>
          </Tooltip>
        </Flex>
      </Box>
    </>
  );
});

UserMenuPanel.displayName = 'UserMenuPanel';

export default UserMenuPanel;
